import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import FormItem from '../../../components/Form/FormItem';
import FieldSet from '../../../components/Form/FieldSet';
import Label from '../../../components/Form/Label';
import Input from '../../../components/Form/Input';
import Select from '../../../components/Form/Select';
import Title from '../../../components/Title';
import { UserData } from '../../../types/userData';
import inputMask from '../../../util/mask';
import { api } from '../../../services/api';
import { useAuth } from '../../../hooks/useAuth';
import { listCountries } from '../../../config/countriesSearchedStates';
import { isUnprefixedAndValidCep, isValidCep } from '../../../util/validation';
import { countriesMock } from '../../../util/countries';
import { statesMock } from '../../../util/states';
import { citiesMock } from '../../../util/cities';

type Country = {
  id: number;
  description: string;
  sigla: string;
  ibge: number;
};

type State = {
  id: number;
  description: string;
  acronym: string;
};

type City = {
  id: number;
  description: string;
};

type AddressByZipCode = {
  address: string;
  city: {
    state: { country: Country } & State;
  } & City;
  neighborhood: string;
  zipcode: string;
};

export interface Props {
  title?: string;
}

const AddressForm = ({ title }: Props) => {
  const { values, handleChange, errors, setValues } =
    useFormikContext<UserData>();
  const [countries, setCountries] = useState<Array<Country>>([]);
  const [states, setStates] = useState<Array<State>>([]);
  const [cities, setCities] = useState<Array<City>>([]);
  const [addressLoadedByCep, setAddressLoadedByCep] = useState<boolean>(false);
  const [canWriteAddress, setCanWriteAddress] = useState<boolean>(true);
  const [openFormComplete, setOpenFormComplete] = useState<boolean>(false);

  const { authenticateOnAPI } = useAuth();

  const setFieldAddress = (fields: any) => {
    return setValues({
      ...values,
      address: {
        ...values.address,
        ...fields,
      },
    });
  };

  useEffect(() => {
    let isMounted = true;

    const getAllCountries = async () => {
      const token = await authenticateOnAPI();
      api
        .get('/address/countries', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response: AxiosResponse<Array<Country>>) => {
          if (isMounted) {
            setCountries(response.data);
          }
        })
        .catch(() => {
          setCountries(countriesMock);
          if (countriesMock.length === 0) {
            toast.error('Erro ao carregar lista de países');
            setStates([]);
            setCities([]);
          }
        });
    };

    getAllCountries();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted: boolean;
    isMounted = true;

    const getStatesByCountry = async () => {
      const countryId = values.address.country.id;
      const token = await authenticateOnAPI();
      api
        .get(`/address/states/by/country?country=${countryId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response: AxiosResponse<Array<State>>) => {
          if (isMounted) {
            setStates(response.data);
          }
        })
        .catch(() => {
          setStates(statesMock);
          if (statesMock.length === 0) {
            toast.error('Erro ao carregar lista de estados');
            setCities([]);
          }
        });
    };

    if (listCountries.includes(values.address.country.name)) {
      setOpenFormComplete(!openFormComplete);
      getStatesByCountry();
    } else {
      setOpenFormComplete(false);
    }
    return () => {
      isMounted = false;
    };
  }, [
    values.address.country.id,
    values.address.country.name,
    authenticateOnAPI,
  ]);

  useEffect(() => {
    let isMounted = true;

    const getCitiesByState = async () => {
      const stateId = values.address.state.id;
      const token = await authenticateOnAPI();
      api
        .get(`/address/cities/by/state?state=${stateId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response: AxiosResponse<Array<State>>) => {
          if (isMounted) {
            setCities(response.data);
          }
        })
        .catch(() => {
          setCities(citiesMock.filter(element => element.state_id === stateId));
          if (citiesMock.length === 0) {
            toast.error('Erro ao carregar lista de cidades');
            setCities([]);
          }
        });
    };

    if (values.address.state.name) {
      getCitiesByState();
    }
    return () => {
      isMounted = false;
    };
  }, [values.address.state.name, values.address.state.id]);

  useEffect(() => {
    let isMounted = true;

    const getAddressByZipCode = async () => {
      const token = await authenticateOnAPI();

      const zipCode = inputMask.unMask(values.address.zipcode);

      api
        .get(`/address/by/zipcode?zipcode=${zipCode}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response: AxiosResponse<AddressByZipCode>) => {
          const { data } = response;

          const neighborhood =
            values.address.neighborhood &&
            isUnprefixedAndValidCep(values.address.zipcode)
              ? values.address.neighborhood
              : data.neighborhood;

          const address1 =
            values.address.address1 &&
            isUnprefixedAndValidCep(values.address.zipcode)
              ? values.address.address1
              : data.address;

          if (isMounted) {
            const address = {
              neighborhood,
              zipcode: inputMask.cep(data.zipcode),
              address1,
              city: {
                name: data.city.description,
                id: data.city.id,
              },
              state: {
                name: data.city.state.description,
                id: data.city.state.id,
              },
              country: {
                id: data.city.state.country.id,
                name: data.city.state.country.description,
              },
            };

            setFieldAddress(address);
            setAddressLoadedByCep(true);
            setCanWriteAddress(false);
          }
        })
        .catch(() => {
          setCanWriteAddress(true);
        });
    };

    if (isValidCep(values.address.zipcode)) {
      getAddressByZipCode();
    }

    if (values.address.zipcode === '') {
      setAddressLoadedByCep(false);
      setCanWriteAddress(true);
    }

    return () => {
      isMounted = false;
    };
  }, [values.address.zipcode]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value, options, selectedIndex } = e.target;
    const address = {
      neighborhood: '',
      zipcode: '',
      address1: '',
      address2: '',
      number: '',
      city: {
        name: '',
        id: 0,
      },
      state: {
        name: '',
        id: 0,
      },
      country: {
        id: parseInt(value, 10),
        name: options[selectedIndex].text,
      },
    };

    if (!value) {
      address.country.id = 0;
      address.country.name = '';
    }

    setFieldAddress(address);
    setCities([]);
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value, options, selectedIndex } = e.target;
    const stateChange = {
      state: {
        id: parseInt(value, 10),
        name: options[selectedIndex].text,
      },
      city: {
        id: 0,
        name: '',
      },
    };
    if (!value) {
      stateChange.state.id = 0;
      stateChange.state.name = '';
      setCities([]);
    }

    setFieldAddress(stateChange);
  };

  const addressRequired = () => Boolean(values.address.zipcode);

  return (
    <FormItem>
      {title && <Title>{title}</Title>}
      <FieldSet>
        <Label htmlFor="country">País</Label>
        <Select
          id="country"
          name="address.country.id"
          value={values.address.country.id || ''}
          onChange={handleCountryChange}
        >
          <option value="">Selecione um país</option>
          {countries &&
            countries.map(country => {
              return (
                <option value={country.id} key={country.id}>
                  {country.description}
                </option>
              );
            })}
        </Select>
      </FieldSet>
      {openFormComplete && (
        <>
          <FieldSet>
            <Label htmlFor="zipcode">CEP</Label>
            <Input
              id="zipcode"
              maxLength={10}
              onChange={handleChange}
              value={inputMask.cep(values.address.zipcode) || ''}
              name="address.zipcode"
              autoComplete="new-zipcode"
              placeHolderText="Informe o seu CEP"
              errorMessage={
                errors.address?.zipcode ? errors.address?.zipcode : ''
              }
            />
          </FieldSet>
          <FieldSet>
            <Label htmlFor="address1">
              Logradouro (rua, avenida, praça, etc)
            </Label>
            <Input
              maxLength={110}
              id="address1"
              onChange={handleChange}
              value={values.address.address1 || ''}
              disabled={
                addressLoadedByCep &&
                !canWriteAddress &&
                !isUnprefixedAndValidCep(values.address.zipcode)
              }
              name="address.address1"
              placeHolderText="Informe seu logradouro"
              required={addressRequired()}
              errorMessage={
                values.address?.zipcode && !values.address?.address1
                  ? 'O logradouro precisa ser preenchido.'
                  : errors.address?.address1
              }
            />
          </FieldSet>
          <FieldSet>
            <Label htmlFor="number">Número</Label>
            <Input
              id="number"
              type="number"
              onChange={handleChange}
              pattern="[0-9]*"
              value={values.address.number || ''}
              name="address.number"
              placeHolderText="Informe o número do seu logradouro"
              required={addressRequired()}
              errorMessage={
                values.address?.zipcode && !values.address?.number
                  ? 'O número precisa ser preenchido.'
                  : errors.address?.number
              }
            />
          </FieldSet>
          <FieldSet>
            <Label htmlFor="address2">Complemento</Label>
            <Input
              maxLength={90}
              id="address2"
              onChange={handleChange}
              value={values.address.address2 || ''}
              name="address.address2"
              placeHolderText="Informe o complemento do seu logradouro"
            />
          </FieldSet>
          <FieldSet>
            <Label htmlFor="neighborhood">Bairro</Label>
            <Input
              maxLength={114}
              id="neighborhood"
              onChange={handleChange}
              value={values.address.neighborhood || ''}
              disabled={
                addressLoadedByCep &&
                !canWriteAddress &&
                !isUnprefixedAndValidCep(values.address.zipcode)
              }
              name="address.neighborhood"
              placeHolderText="Informe o seu bairro"
              required={addressRequired()}
              errorMessage={
                values.address?.zipcode && !values.address?.neighborhood
                  ? 'O bairro precisa ser preenchido.'
                  : errors.address?.neighborhood
              }
            />
          </FieldSet>
          <FieldSet>
            <Label htmlFor="state">Estado</Label>
            <Select
              id="state"
              name="address.state.name"
              value={values.address.state.id || ''}
              disabled={addressLoadedByCep && !canWriteAddress}
              onChange={handleStateChange}
              errorMessage={
                errors.address?.state?.name
                  ? 'O estado precisa ser preenchido.'
                  : errors.address?.state?.name
              }
            >
              <option value="">Selecione um estado</option>
              {states &&
                states.map(state => {
                  return (
                    <option
                      value={state.id}
                      id={state.id.toString()}
                      key={state.id}
                    >
                      {state.description}
                    </option>
                  );
                })}
            </Select>
          </FieldSet>
          <FieldSet>
            <Label htmlFor="city">Cidade</Label>
            <Select
              id="city"
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const { value, options, selectedIndex } = e.target;
                const city = {
                  id: parseInt(value, 10),
                  name: options[selectedIndex].text,
                };

                if (!value) {
                  city.id = 0;
                  city.name = '';
                }
                setFieldAddress({ city });
              }}
              value={values.address?.city?.id || ''}
              disabled={addressLoadedByCep && !canWriteAddress}
              name="address.city.name"
              errorMessage={
                errors.address?.city?.name
                  ? 'A cidade precisa ser preenchida.'
                  : errors.address?.city?.name
              }
            >
              {cities.length > 0 && (
                <>
                  <option value="">Selecione uma Cidade</option>
                  {cities.map(city => {
                    return (
                      <option
                        value={city.id}
                        id={city.id.toString() || ''}
                        key={city.id}
                      >
                        {city.description}
                      </option>
                    );
                  })}
                </>
              )}
            </Select>
          </FieldSet>
        </>
      )}
    </FormItem>
  );
};

export default AddressForm;
